// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * Shared wiring for the Live view's *composed* endpoints — the ones where the
 * BFF does the fan-out, so one poll fetches a whole card's series.
 *
 * Two poll-window details are load-bearing: the window end stays out of the
 * query key (keying on a ticking clock made every poll a cache miss, and the
 * curve blinked once a minute), and the clock is read inside `queryFn` so the
 * window resolves at fetch time rather than during render.
 *
 * Each poll asks only for the tail and folds it into the cache entry, exactly as
 * {@link useLiveLogicalNodeSeries} does. Re-asking for the whole plan day meant
 * nine (chillers) or thirty-six (HVAC) upstream reads across `[midnight, now]`
 * every minute to learn one new bucket, and it made the card hostage to the
 * worst of forty-five reads: the BFF absorbs a per-device 503 and answers 200
 * with a short sum, so `keepPreviousData` never fired and the degraded payload
 * replaced a good curve outright. {@link mergeComposedWindow} holds the good
 * one; see its rules.
 */

import { useMemo, useRef } from 'react';
import { keepPreviousData, useQuery, useQueryClient } from '@tanstack/react-query';

import type { LiveSeriesCoverage } from './useLivePlanTelemetry';
import {
  deltaWindowStart,
  isServerFault,
  liveBackoffInterval,
  pollWindowEnd,
} from '../utils/pollWindow';
import { axisStartMs } from '../utils/planSeries';
import { mergeComposedWindow } from '../utils/telemetryMerge';
import type { ComposedWindow } from '../utils/telemetryMerge';
import { resampleToAxis, resampleToAxisDominant } from '../utils/telemetryResample';
import type { TimeAxis } from '../types';

export const EMPTY_SERIES: (number | null)[] = [];
export const NO_IDS: string[] = [];

/** The parts of a composed payload this hook reads; the rest is caller-specific. */
export interface ComposedPayload {
  timestamps: string[];
  charts: unknown;
  coverage: {
    requested: number;
    contributing: number;
    unknown?: string[];
    unresolved?: string[];
    failed?: string[];
  };
}

/** Resamplers bound to the payload's own axis. `onAxisDominant` takes the
 *  longest-held value per bucket rather than the mean, for discrete series. */
export interface AxisHelpers {
  onAxis: (_values: (number | null)[]) => (number | null)[];
  onAxisDominant: (_values: (number | null)[]) => (number | null)[];
}

export function useLiveComposedSeries<TPayload extends ComposedPayload, TSeries extends object>({
  name,
  keyParts,
  gate = true,
  timeAxis,
  fetch,
  emptySeries,
  select,
}: {
  /** Query-key discriminator, e.g. `'chiller-plant'`. */
  name: string;
  /** Ids the response varies by; all must be present for the query to fire. */
  keyParts: (string | undefined)[];
  gate?: boolean;
  timeAxis: TimeAxis | undefined;
  fetch: (_window: { start: string; end: string }) => Promise<{ data?: TPayload } | undefined>;
  emptySeries: TSeries;
  /** Map the payload onto the plan axis; `primary` is what coverage reports on. */
  select: (
    _payload: TPayload,
    _helpers: AxisHelpers
  ) => { series: TSeries; primary: (number | null)[] };
}): { data: TPayload | undefined; loading: boolean; coverage: LiveSeriesCoverage } & TSeries {
  const queryClient = useQueryClient();
  const axisStart = timeAxis ? axisStartMs(timeAxis) : undefined;
  const start = axisStart !== undefined ? new Date(axisStart).toISOString() : undefined;
  const enabled = Boolean(gate && start && timeAxis && keyParts.every(Boolean));
  const queryKey = ['live', name, ...keyParts.map(part => part ?? ''), start ?? ''];

  // Polls in a row that failed with a server fault: the next wait grows with it.
  const failures = useRef(0);
  const query = useQuery({
    queryKey,
    queryFn: async () => {
      const previous = queryClient.getQueryData<ComposedWindow<TPayload>>(queryKey);
      const end = pollWindowEnd(new Date());
      const from = deltaWindowStart(start as string, previous?.fetchedThrough);
      try {
        const envelope = await fetch({ start: from, end });
        failures.current = 0;
        return mergeComposedWindow(previous, envelope?.data, {
          end,
          dropBefore: start as string,
        });
      } catch (error) {
        if (isServerFault(error)) failures.current += 1;
        throw error;
      }
    },
    enabled,
    refetchInterval: () => liveBackoffInterval(failures.current),
    placeholderData: keepPreviousData,
  });

  const window = query.data ?? undefined;
  const data = window?.payload;
  const loading = query.isLoading;
  const pollFailed = query.isError && Boolean(data);
  const requestFailed = query.isError && !data;
  const degraded = window?.degraded ?? NO_IDS;

  return useMemo(() => {
    if (!data || !timeAxis) {
      return {
        data,
        loading,
        ...emptySeries,
        coverage: {
          values: EMPTY_SERIES,
          requested: 0,
          contributing: 0,
          unknown: NO_IDS,
          unresolved: NO_IDS,
          failed: NO_IDS,
          stale: NO_IDS,
          requestFailed,
          loading,
        },
      };
    }

    const { timestamps, coverage } = data;
    const points = (values: (number | null)[]) =>
      timestamps.map((timestamp, index) => ({ timestamp, value: values[index] ?? null }));
    const { series, primary } = select(data, {
      onAxis: values => resampleToAxis(points(values), timeAxis),
      onAxisDominant: values => resampleToAxisDominant(points(values), timeAxis),
    });

    return {
      data,
      loading,
      ...series,
      coverage: {
        values: primary,
        requested: coverage.requested,
        contributing: coverage.contributing,
        unknown: coverage.unknown ?? NO_IDS,
        unresolved: coverage.unresolved ?? NO_IDS,
        failed: coverage.failed ?? NO_IDS,
        stale: degraded,
        pollFailed,
        loading,
      },
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, timeAxis, loading, degraded, pollFailed, requestFailed]);
}
